import { parseFrontMatter } from './frontMatter';
import type { ContentDoc, ContentIndex, ContentProblem, Shell, Tier } from './types';

/**
 * Turns raw file contents into a checked index. Pure, so a test can hand it a
 * few strings and the apps can hand it whatever Vite globbed.
 */

const SHELLS: Shell[] = ['guest', 'ops'];
const TIERS: Tier[] = ['all_inclusive', 'standard'];

/**
 * Files that live under content/ and are not pages (defect D11).
 *
 * The real exclusion is in the Vite glob in load.ts, because `eager` inlines a
 * file's text into the bundle whether or not the index keeps it. This list is
 * the second line: it keeps a working note out of the index and out of the
 * "every page needs both languages" check, whatever handed us the files.
 */
const NOT_PAGES = ['PLACEHOLDERS.md', 'README.md'];

export function isPage(filePath: string): boolean {
  const name = filePath.replace(/\\/g, '/').split('/').pop() ?? '';
  return !NOT_PAGES.includes(name);
}

/** content/guest/arrive/from-the-airport.md becomes guest/arrive/from-the-airport */
export function normalizePath(filePath: string): string {
  return filePath
    .replace(/^(?:.*\/)?content\//, '')
    .replace(/\\/g, '/')
    .replace(/\.(md|markdown|json)$/, '');
}

export function toDoc(filePath: string, source: string): ContentDoc | ContentProblem {
  const path = normalizePath(filePath);
  const { data, body } = parseFrontMatter(source);

  const titleEs = typeof data.title_es === 'string' ? data.title_es : '';
  const titleEn = typeof data.title_en === 'string' ? data.title_en : '';
  if (!titleEs || !titleEn) {
    return {
      path,
      problem: 'needs both title_es and title_en: a page in one language is a page half the island cannot read',
    };
  }

  const shellFromPath = path.split('/')[0];
  const shell = (typeof data.shell === 'string' ? data.shell : shellFromPath) as Shell;
  if (!SHELLS.includes(shell)) {
    return { path, problem: `shell must be guest or ops, got ${String(shell)}` };
  }

  const tiers = toArray(data.tiers).filter((t): t is Tier => TIERS.includes(t as Tier));

  return {
    path,
    slug: typeof data.slug === 'string' && data.slug ? data.slug : (path.split('/').pop() ?? path),
    shell,
    section: typeof data.section === 'string' ? data.section : (path.split('/')[1] ?? 'general'),
    order: typeof data.order === 'number' ? data.order : 100,
    title: { es: titleEs, en: titleEn },
    lede:
      typeof data.lede_es === 'string' && typeof data.lede_en === 'string'
        ? { es: data.lede_es, en: data.lede_en }
        : undefined,
    tiers,
    offline: data.offline === true,
    meta: data,
    body,
  };
}

function toArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string' && value) return [value];
  return [];
}

function isProblem(value: ContentDoc | ContentProblem): value is ContentProblem {
  return 'problem' in value;
}

export function collectProblems(files: Record<string, string>): ContentProblem[] {
  return Object.entries(files)
    .filter(([path]) => isPage(path))
    .map(([path, source]) => toDoc(path, source))
    .filter(isProblem);
}

/**
 * Builds the index both apps read.
 *
 * A page that fails its checks is left out rather than crashing the app: a
 * guest standing on a dock should not get a white screen because somebody
 * forgot a title. The build time test in this package is what actually stops a
 * broken page from shipping.
 */
export function buildIndex(files: Record<string, string>): ContentIndex {
  const docs = Object.entries(files)
    .filter(([path]) => isPage(path))
    .map(([path, source]) => toDoc(path, source))
    .filter((value): value is ContentDoc => !isProblem(value))
    .sort((a, b) => a.order - b.order || a.path.localeCompare(b.path));

  const byPath = new Map(docs.map((doc) => [doc.path, doc]));
  const bySection = new Map<string, ContentDoc[]>();
  for (const doc of docs) {
    const list = bySection.get(doc.section) ?? [];
    list.push(doc);
    bySection.set(doc.section, list);
  }

  return {
    all: docs,
    byPath,
    bySection,
    get: (path) => byPath.get(path),
    section: (name) => bySection.get(name) ?? [],
    forTier: (tier) => docs.filter((doc) => doc.tiers.length === 0 || (tier && doc.tiers.includes(tier))),
  };
}
